import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FranchiseFeePayoutDataParams,
  FranchiseFeePayoutsQueryKey,
  createFranchiseFeePayout as createPayout,
  deleteFranchiseFeePayout as deletePayout,
  fetchFranchiseFeePayout,
  fetchFranchiseFeePayouts,
  fetchFranchiseFeePayoutsQueryKey,
  updateFranchiseFeePayout as updatePayout,
} from 'src/models/hq/franchise-fee-payout/franchise-fee-payout';
import { useListSearchParams } from 'src/utils/useListSearchParams';

export const useFranchiseFeePayoutPage = (id?: string) => {
  const queryClient = useQueryClient();

  const {
    params,
    updateParam,
    filterParams,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams({
    paramPrefix: 'franchise_fee_payout_',
    defaultPerPage: 20,
  });

  const {
    data: payouts = { franchiseFeePayouts: [], total: 0 },
    isLoading,
    isError,
  } = useQuery({
    queryKey: fetchFranchiseFeePayoutsQueryKey({
      page: params.page,
      perPage: params.perPage,
      search: params.search,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      filters: {
        payoutStatus: filterParams.payoutStatus,
        payoutPeriodRange: filterParams.payoutPeriodRange,
        sites: filterParams.sites,
      },
    }),
    queryFn: () =>
      fetchFranchiseFeePayouts({
        page: params.page,
        perPage: params.perPage,
        search: params.search,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
        filters: {
          payoutStatus: filterParams.payoutStatus,
          payoutPeriodRange: filterParams.payoutPeriodRange,
          sites: filterParams.sites,
        },
      }),
  });

  // Fetch single franchise fee payout for edit page
  const {
    data: franchiseFeePayout,
    isLoading: franchiseFeePayoutIsLoading,
    isError: franchiseFeePayoutIsError,
  } = useQuery({
    queryKey: [FranchiseFeePayoutsQueryKey, id],
    queryFn: () => fetchFranchiseFeePayout(id!),
    enabled: !!id,
  });

  // Create mutation
  const createFranchiseFeePayout = useMutation({
    mutationFn: createPayout,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FranchiseFeePayoutsQueryKey],
      });
    },
  });

  // Update mutation
  const updateFranchiseFeePayout = useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: string } & FranchiseFeePayoutDataParams) => updatePayout(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FranchiseFeePayoutsQueryKey],
      });
    },
  });

  // Delete mutation
  const deleteFranchiseFeePayout = useMutation({
    mutationFn: deletePayout,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FranchiseFeePayoutsQueryKey],
      });
    },
  });

  return {
    payouts: payouts.franchiseFeePayouts,
    payoutsTotal: payouts.total,
    payoutsIsLoading: isLoading,
    payoutsIsError: isError,
    franchiseFeePayout,
    franchiseFeePayoutIsLoading,
    franchiseFeePayoutIsError,
    createFranchiseFeePayout,
    updateFranchiseFeePayout,
    deleteFranchiseFeePayout,
    params,
    updateParam,
    filterParams,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  };
};
